import SparkMD5 from "spark-md5";
import { wolfApi } from "@/lib/axios";

export type SupportedProfileLanguage = "ja" | "en";

export interface WolfProfile {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  language: SupportedProfileLanguage;
  profileImageUrl: string | null;
  coverImageUrl: string | null;
  grantedLicenseTypes: string[];
  profileQualityIssues?: { required: string[]; recommended: string[] };
}

export interface WolfProfileUpdate {
  firstName: string;
  lastName: string;
  language: SupportedProfileLanguage;
  profileImageSignedBlobId?: string;
  coverImageSignedBlobId?: string;
}

export interface WolfEmailChangeInput {
  email: string;
  currentPassword: string;
}

type DirectUploadResponse = {
  signed_id: string;
  direct_upload: {
    url: string;
    headers: Record<string, string>;
  };
};

type WolfSessionResponse = {
  id: number;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  language?: string | null;
  profile_image_url?: string | null;
  cover_image_url?: string | null;
  granted_license_types?: string[];
  profile_quality_issues?: { required?: string[]; recommended?: string[] };
};

const UPDATE_PROFILE_MUTATION = `
  mutation updateUserProfile($input: UpdateUserProfileInput!) {
    updateUserProfile(input: $input) {
      success
      userErrors { message code path }
      user { id firstName lastName language profileImageUrl coverImageUrl }
    }
  }
`;

const REQUEST_EMAIL_CHANGE_MUTATION = `
  mutation requestEmailChange($input: RequestEmailChangeInput!) {
    requestEmailChange(input: $input) {
      success
      pendingEmail
      userErrors { message code path }
    }
  }
`;

export const loadWolfProfile = async (): Promise<WolfProfile> => {
  const { data } = await wolfApi.get<WolfSessionResponse>(
    "/api/v1/auth/sessions",
    { params: { with_permission: true } },
  );

  return {
    id: data.id,
    email: data.email,
    firstName: data.first_name ?? "",
    lastName: data.last_name ?? "",
    language: data.language === "en" ? "en" : "ja",
    profileImageUrl: data.profile_image_url ?? null,
    coverImageUrl: data.cover_image_url ?? null,
    grantedLicenseTypes: data.granted_license_types ?? [],
    profileQualityIssues: {
      required: data.profile_quality_issues?.required ?? [],
      recommended: data.profile_quality_issues?.recommended ?? [],
    },
  };
};

export const saveWolfProfile = async (
  input: WolfProfileUpdate,
): Promise<WolfProfile> => {
  const { data } = await wolfApi.post<{
    data?: {
      updateUserProfile?: {
        success: boolean;
        userErrors: Array<{ message: string }>;
        user: Omit<
          WolfProfile,
          "email" | "grantedLicenseTypes" | "profileQualityIssues"
        > | null;
      };
    };
    errors?: Array<{ message: string }>;
  }>("/api/v1/graphql", {
    query: UPDATE_PROFILE_MUTATION,
    variables: { input },
  });
  const result = data.data?.updateUserProfile;
  if (!result?.success || !result.user) {
    throw new Error(
      result?.userErrors[0]?.message ??
        data.errors?.[0]?.message ??
        "Profile update failed",
    );
  }
  const current = await loadWolfProfile();
  return { ...current, ...result.user };
};

export const requestWolfEmailChange = async (
  input: WolfEmailChangeInput,
): Promise<void> => {
  const { data } = await wolfApi.post<{
    data?: {
      requestEmailChange?: {
        success: boolean;
        userErrors: Array<{ message: string }>;
      };
    };
    errors?: Array<{ message: string }>;
  }>("/api/v1/graphql", {
    query: REQUEST_EMAIL_CHANGE_MUTATION,
    variables: { input },
  });
  const result = data.data?.requestEmailChange;
  if (!result?.success) {
    throw new Error(
      result?.userErrors[0]?.message ??
        data.errors?.[0]?.message ??
        "Email change request failed",
    );
  }
};

const uploadWolfImage = async (
  purpose: "profile_image" | "cover_image",
  file: File,
): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const checksum = btoa(SparkMD5.ArrayBuffer.hash(buffer, true));
  const { data } = await wolfApi.post<DirectUploadResponse>(
    "/api/v1/direct_uploads",
    {
      direct_upload: {
        purpose,
        filename: file.name,
        byte_size: file.size,
        checksum,
        content_type: file.type,
      },
    },
  );
  await wolfApi.put(data.direct_upload.url, file, {
    headers: data.direct_upload.headers,
    transformRequest: [(body) => body],
  });
  return data.signed_id;
};

export const uploadWolfProfileImage = (file: File) =>
  uploadWolfImage("profile_image", file);

export const uploadWolfCoverImage = (file: File) =>
  uploadWolfImage("cover_image", file);
